import type React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, FormHelp, Input, Label, Spacer } from '@zillow/constellation';
import { StackedChildren } from '@zillow/zrs-comet';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { WATER_SEWER } from '../../../../constants';
import HeaderToggle from './HeaderToggle';
import FixedOrVariableSelector from './FixedOrVariableSelector';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [WATER_SEWER.FIELD_NAME.IS_ENABLED]: true,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: '',
};

export default function WaterSewer_v1({ section, setSectionValues }: Props): JSX.Element {
  const option1 = WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY;
  const waterSewer_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED);
  const option1Selected = section.getFieldValue(option1);
  const option2Selected = section.getFieldValue(option2);

  function setAdditionalFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [WATER_SEWER.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED),
    });
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <FixedOrVariableSelector
        legendText="What is the additional fee for water/sewer?"
        fixedAmountSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
        fixedAmountValue={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE)}
        setFixedAmountSelected={setAdditionalFixedAmountSelected}
        setFixedAmountValue={setAdditionalFixedAmountValue}
        variableMonthlyCostSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED)}
        setVariableMonthlySelected={setAdditionalVariableCostSelected}
      />
    );
  }

  function renderOption1Details(): React.ReactNode {
    return (
      <FormField
        label={<Label id="service-provider">What service provider?</Label>}
        control={
          <Input
            aria-labelledby="service-provider"
            placeholder="(example: Wonderland Waterworks)"
            data-test="service-provider"
            value={section.getFieldValue(WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER)}
            onChange={setServiceProvider}
          />
        }
        description={
          <FormHelp>Hot water costs may be included in electricity or natural gas costs as applicable</FormHelp>
        }
      />
    );
  }

  return (
    <StackedChildren size="xs">
      <HeaderToggle
        header="Water/sewer"
        subheader="Tenant pays in addition to base rent"
        switchOn={waterSewer_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {waterSewer_isEnabled && (
        <>
          <CenteredHR />
          <Spacer marginBottom="sm">
            <FormField
              label={<Label>Who will be responsible for water/sewer set up?</Label>}
              control={
                <RadioListBox
                  section={section}
                  setSectionValues={setSectionValues}
                  option1FieldName={option1}
                  option2FieldName={option2}
                  option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                  option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                  option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                  option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                />
              }
            />
          </Spacer>
          {option1Selected && renderOption1Details()}
          {option2Selected && renderOption2Details()}
        </>
      )}
    </StackedChildren>
  );
}
